import { useState, useEffect } from 'react';

// ============================================================================
// TIPOS DE DISPOSITIVO
// ============================================================================

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

export interface DeviceInfo {
  type: DeviceType;
  orientation: Orientation;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  userAgent: string;
  isTouchDevice: boolean;
}

// ============================================================================
// CONSTANTES DE BREAKPOINTS
// ============================================================================

const BREAKPOINTS = {
  MOBILE: 640,    // sm breakpoint de Tailwind
  TABLET: 1024,   // lg breakpoint de Tailwind  
  DESKTOP: 1280,  // xl breakpoint de Tailwind
} as const;

// ============================================================================
// HOOK DE DETECCIÓN DE DISPOSITIVO
// ============================================================================

export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    // Estado inicial del servidor (SSR-safe)
    if (typeof window === 'undefined') {
      return {
        type: 'desktop',
        orientation: 'landscape',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        screenWidth: 1920,
        screenHeight: 1080,
        userAgent: '',
        isTouchDevice: false,
      };
    }

    return getDeviceInfo();
  });

  useEffect(() => {
    // Función para actualizar la información del dispositivo
    const updateDeviceInfo = () => {
      setDeviceInfo(getDeviceInfo());
    };

    // Event listeners para cambios de tamaño y orientación
    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
};

// ============================================================================
// FUNCIÓN AUXILIAR PARA OBTENER INFORMACIÓN DEL DISPOSITIVO
// ============================================================================

const getDeviceInfo = (): DeviceInfo => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const userAgent = navigator.userAgent;

  // Detectar tipo de dispositivo basado en el ancho de pantalla
  let type: DeviceType;
  if (width < BREAKPOINTS.MOBILE) {
    type = 'mobile';
  } else if (width < BREAKPOINTS.TABLET) {
    type = 'tablet';
  } else {
    type = 'desktop';
  }

  // Detectar orientación
  const orientation: Orientation = width > height ? 'landscape' : 'portrait';

  // Detectar si es dispositivo táctil
  const isTouchDevice = 'ontouchstart' in window || 
                       navigator.maxTouchPoints > 0 || 
                       /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

  return {
    type,
    orientation,
    isMobile: type === 'mobile',
    isTablet: type === 'tablet',
    isDesktop: type === 'desktop',
    screenWidth: width,
    screenHeight: height,
    userAgent,
    isTouchDevice,
  };
};

// ============================================================================
// HOOKS ESPECÍFICOS PARA FACILITAR EL USO
// ============================================================================

/**
 * Hook que solo retorna si es móvil (compatible con el hook existente)
 */
export const useMobile = (): boolean => {
  const { isMobile } = useDeviceDetection();
  return isMobile;
};

/**
 * Hook que retorna si es tablet
 */
export const useTablet = (): boolean => {
  const { isTablet } = useDeviceDetection();
  return isTablet;
};

/**
 * Hook que retorna si es desktop
 */
export const useDesktop = (): boolean => {
  const { isDesktop } = useDeviceDetection();
  return isDesktop;
};

/**
 * Hook que retorna la orientación actual
 */
export const useOrientation = (): Orientation => {
  const { orientation } = useDeviceDetection();
  return orientation;
};

/**
 * Hook que retorna si es un dispositivo táctil
 */
export const useTouchDevice = (): boolean => {
  const { isTouchDevice } = useDeviceDetection();
  return isTouchDevice;
};

/**
 * Hook que retorna las dimensiones de la pantalla
 */
export const useScreenSize = (): { width: number; height: number } => {
  const { screenWidth, screenHeight } = useDeviceDetection();
  return { width: screenWidth, height: screenHeight };
};

// ============================================================================
// UTILIDADES ADICIONALES
// ============================================================================

/**
 * Función para detectar si el dispositivo es móvil basado en user agent
 */
export const isMobileUserAgent = (userAgent: string): boolean => {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
};

/**
 * Función para detectar si el dispositivo es tablet basado en user agent
 */
export const isTabletUserAgent = (userAgent: string): boolean => {
  return /iPad|Android(?=.*Tablet)|Windows NT(?=.*Touch)/i.test(userAgent);
};

/**
 * Función para detectar el sistema operativo
 */
export const getOperatingSystem = (userAgent: string): string => {
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Mac/i.test(userAgent)) return 'macOS';
  if (/Linux/i.test(userAgent)) return 'Linux';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  return 'Unknown';
};

/**
 * Función para detectar el navegador
 */
export const getBrowser = (userAgent: string): string => {
  if (/Chrome/i.test(userAgent) && !/Edge/i.test(userAgent)) return 'Chrome';
  if (/Firefox/i.test(userAgent)) return 'Firefox';
  if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) return 'Safari';
  if (/Edge/i.test(userAgent)) return 'Edge';
  if (/Opera/i.test(userAgent)) return 'Opera';
  return 'Unknown';
};
